import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import type { MssCreateRequest, MssLegendEntry, MssLine } from '@produzionejb/types';
import { ApiError } from '../../lib/api-client';
import {
  useCreateMss,
  useMss,
  useUpdateMss,
  useCloseMss,
  useDeleteMss,
  useLegendEntries,
} from '../../lib/mss-api';
import { LotPicker } from '../../components/LotPicker';
import { CustomerPicker } from '../../components/CustomerPicker';
import { LotAnalisiPicker } from '../../components/LotAnalisiPicker';
import { ProductSkuPicker } from '../../components/ProductSkuPicker';

type LineRow = {
  key: string;
  packageNumber: string;
  lotId: string;
  lotCode?: string;
  lotProductId: string | null;
  lotAnalisiId: string | null;
  lotAnalisiCodice?: string | null;
  sku: string;
  confezione: string;
  qty5g: string;
  qty10g: string;
  qty20g: string;
  qty50g: string;
  qty100g: string;
  qty: string;
  note: string;
};

const GRAMMATURE_FIELDS = ['qty5g', 'qty10g', 'qty20g', 'qty50g', 'qty100g'] as const;
const GRAMMATURE_VALUES: Record<(typeof GRAMMATURE_FIELDS)[number], number> = {
  qty5g: 5,
  qty10g: 10,
  qty20g: 20,
  qty50g: 50,
  qty100g: 100,
};

// Identifica la grammatura "dominante" della riga: quella con qty non-zero maggiore.
// Es. qty5g=2, qty10g=1, qty50g=0 -> dominante=5g. Usato per auto-resolve SKU.
function dominantGrammatura(r: LineRowLike): number | null {
  let bestGr: number | null = null;
  let bestQty = 0;
  for (const f of GRAMMATURE_FIELDS) {
    const q = Number(r[f]) || 0;
    if (q > bestQty) {
      bestQty = q;
      bestGr = GRAMMATURE_VALUES[f];
    }
  }
  return bestGr;
}

type LineRowLike = Pick<LineRow, 'qty5g' | 'qty10g' | 'qty20g' | 'qty50g' | 'qty100g'>;

type LegendRow = {
  key: string;
  k: string;
  v: string;
};

function newKey() {
  return Math.random().toString(36).slice(2, 9);
}

function todayIso() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function computedWeight(r: LineRow): number {
  return (
    Number(r.qty5g) * 5 +
    Number(r.qty10g) * 10 +
    Number(r.qty20g) * 20 +
    Number(r.qty50g) * 50 +
    Number(r.qty100g) * 100
  );
}

function rowToLine(r: LineRow): MssLine {
  // Se qty è vuoto, usa il calcolato. Altrimenti rispetta l'input utente.
  const qtyEff = r.qty.trim() === '' ? computedWeight(r) : Number(r.qty);
  return {
    lotId: r.lotId,
    lotAnalisiId: r.lotAnalisiId,
    packageNumber: r.packageNumber.trim() === '' ? null : Number(r.packageNumber),
    qty: qtyEff,
    qty5g: Number(r.qty5g) || 0,
    qty10g: Number(r.qty10g) || 0,
    qty20g: Number(r.qty20g) || 0,
    qty50g: Number(r.qty50g) || 0,
    qty100g: Number(r.qty100g) || 0,
    sku: r.sku.trim() || null,
    confezione: r.confezione.trim() || null,
    note: r.note.trim() || null,
  };
}

export function MssFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const mssQ = useMss(id);
  const createMut = useCreateMss();
  const updateMut = useUpdateMss(id ?? '');
  const closeMut = useCloseMss(id ?? '');
  const deleteMut = useDeleteMss();

  const [draftName, setDraftName] = useState('');
  const [country, setCountry] = useState<'ES' | 'FR'>('ES');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [workDate, setWorkDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineRow[]>([]);
  const [legend, setLegend] = useState<LegendRow[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showExistingLegend, setShowExistingLegend] = useState(false);

  const existingLegendQ = useLegendEntries(country);
  const readOnly = isEdit && (mssQ.data?.status !== 'DRAFT' || mssQ.data?.locked);

  useEffect(() => {
    if (isEdit && mssQ.data) {
      const m = mssQ.data;
      setDraftName(m.draftName ?? '');
      setCountry(m.country);
      setCustomerId(m.customerId);
      setCustomerName(m.customer?.name ?? null);
      setWorkDate(m.workDate.slice(0, 10));
      setNotes(m.notes ?? '');
      setLines(
        m.lines.map((l) => ({
          key: l.id,
          packageNumber: l.packageNumber != null ? String(l.packageNumber) : '',
          lotId: l.lotId ?? '',
          lotCode: l.lotCode ?? undefined,
          lotProductId: null, // backend response non include productId della riga; auto-popolato al prossimo cambio lotto
          lotAnalisiId: l.lotAnalisiId,
          lotAnalisiCodice: l.lotAnalisiCodice,
          sku: l.sku ?? '',
          confezione: l.confezione ?? '',
          qty5g: String(l.qty5g),
          qty10g: String(l.qty10g),
          qty20g: String(l.qty20g),
          qty50g: String(l.qty50g),
          qty100g: String(l.qty100g),
          qty: l.qty,
          note: l.note ?? '',
        })),
      );
      setLegend(
        m.draftLegendEntries.map((e, idx) => ({ key: `${idx}-${e.key}`, k: e.key, v: e.value })),
      );
    }
  }, [isEdit, mssQ.data]);

  function addLine() {
    setLines((rows) => [
      ...rows,
      {
        key: newKey(),
        packageNumber: '',
        lotId: '',
        lotProductId: null,
        lotAnalisiId: null,
        sku: '',
        confezione: '',
        qty5g: '0',
        qty10g: '0',
        qty20g: '0',
        qty50g: '0',
        qty100g: '0',
        qty: '',
        note: '',
      },
    ]);
  }
  function removeLine(key: string) {
    setLines((rows) => rows.filter((r) => r.key !== key));
  }
  function updateLine(key: string, patch: Partial<LineRow>) {
    setLines((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addLegend() {
    setLegend((rows) => [...rows, { key: newKey(), k: '', v: '' }]);
  }
  function removeLegend(key: string) {
    setLegend((rows) => rows.filter((r) => r.key !== key));
  }
  function updateLegend(key: string, patch: Partial<LegendRow>) {
    setLegend((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function buildPayload(): MssCreateRequest {
    return {
      draftName: draftName.trim() || null,
      country,
      customerId,
      workDate: new Date(workDate),
      notes: notes.trim() || null,
      lines: lines.filter((r) => r.lotId).map(rowToLine),
      legendEntries: legend
        .filter((l) => l.k.trim() && l.v.trim())
        .map<MssLegendEntry>((l) => ({ key: l.k.trim(), value: l.v.trim() })),
    };
  }

  async function handleSave() {
    setErrorMsg(null);
    const payload = buildPayload();
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(payload);
      } else {
        const created = await createMut.mutateAsync(payload);
        navigate(`/documents/mss/${created.id}/edit`);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleClose() {
    if (!id) return;
    if (lines.length === 0) {
      setErrorMsg(t('mss.form.errorEmptyLines'));
      return;
    }
    if (!window.confirm(t('mss.form.confirmClose'))) return;
    setErrorMsg(null);
    try {
      await updateMut.mutateAsync(buildPayload());
      await closeMut.mutateAsync();
      navigate('/documents/mss');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  async function handleDelete() {
    if (!id) return;
    const label = mssQ.data?.code ? `MSS ${mssQ.data.code}` : (draftName || 'MSS');
    if (!window.confirm(t('mss.list.confirmDelete', { name: label }))) return;
    try {
      await deleteMut.mutateAsync(id);
      navigate('/documents/mss');
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      setErrorMsg(msg);
    }
  }

  const pacchiCount = useMemo(() => {
    const set = new Set(lines.map((r) => r.packageNumber).filter((p) => p !== ''));
    return set.size;
  }, [lines]);
  const totalQty = useMemo(() => {
    return lines.reduce((acc, r) => {
      const q = r.qty.trim() === '' ? computedWeight(r) : Number(r.qty);
      return acc + (q || 0);
    }, 0);
  }, [lines]);
  const lotsCount = useMemo(() => {
    return new Set(lines.map((r) => r.lotId).filter((l) => l)).size;
  }, [lines]);

  const isSubmitting = createMut.isPending || updateMut.isPending || closeMut.isPending;

  if (isEdit && mssQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (isEdit && mssQ.error) {
    return (
      <p className="text-sm text-red-600">
        {t('mss.form.errorLoad')}: {(mssQ.error as Error).message}
      </p>
    );
  }

  const titleLabel = isEdit
    ? mssQ.data?.code
      ? `${mssQ.data.type} ${mssQ.data.code}`
      : (mssQ.data?.draftName ?? t('mss.form.titleEdit'))
    : t('mss.form.titleNew');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{titleLabel}</h2>
        <div className="flex items-center gap-2">
          {isEdit && mssQ.data && (
            <>
              <span
                className={
                  mssQ.data.status === 'DRAFT'
                    ? 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700'
                    : 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
                }
              >
                {mssQ.data.status === 'DRAFT' ? t('mss.list.statusDraft') : t('mss.list.statusClosed')}
              </span>
              {mssQ.data.locked && <span title={t('mss.form.lockedTip')}>🔒</span>}
            </>
          )}
          <Button variant="secondary" onClick={() => navigate('/documents/mss')}>
            {t('common.back')}
          </Button>
        </div>
      </div>

      {errorMsg && (
        <Card>
          <CardContent>
            <p className="text-sm text-red-600">{errorMsg}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('mss.sections.header')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="draftName">{t('mss.fields.draftName')}</Label>
            <Input
              id="draftName"
              placeholder={t('mss.form.draftNamePlaceholder')}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="country">{t('mss.fields.country')} *</Label>
            <select
              id="country"
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={country}
              onChange={(e) => setCountry(e.target.value as 'ES' | 'FR')}
              disabled={readOnly}
            >
              <option value="ES">{t('mss.country.ES')}</option>
              <option value="FR">{t('mss.country.FR')}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="workDate">{t('mss.fields.workDate')} *</Label>
            <Input
              id="workDate"
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('mss.fields.customer')}</Label>
            <CustomerPicker
              value={customerId}
              valueName={customerName}
              onChange={(id2, name) => {
                setCustomerId(id2);
                setCustomerName(name);
              }}
              disabled={readOnly}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('mss.fields.totals')}</Label>
            <p className="font-mono text-sm">
              {pacchiCount} {t('mss.fields.pacchi')} / {totalQty.toFixed(2)}g / {lotsCount} {t('mss.fields.lotti')}
            </p>
          </div>
          <div className="space-y-1.5 sm:col-span-3">
            <Label htmlFor="notes">{t('mss.fields.notes')}</Label>
            <textarea
              id="notes"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('mss.sections.lines')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {lines.length === 0 && <p className="text-sm text-slate-500">{t('mss.lines.empty')}</p>}
          {lines.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-2 py-2">{t('mss.fields.packageNumber')}</th>
                    <th className="px-2 py-2">{t('mss.fields.lotto')}</th>
                    <th className="px-2 py-2">{t('mss.fields.lotAnalisi')}</th>
                    <th className="px-2 py-2">{t('mss.fields.sku')}</th>
                    <th className="px-2 py-2">{t('mss.fields.confezione')}</th>
                    <th className="px-2 py-2 text-right">5g</th>
                    <th className="px-2 py-2 text-right">10g</th>
                    <th className="px-2 py-2 text-right">20g</th>
                    <th className="px-2 py-2 text-right">50g</th>
                    <th className="px-2 py-2 text-right">100g</th>
                    <th className="px-2 py-2 text-right">{t('mss.fields.qty')}</th>
                    <th className="px-2 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((row) => {
                    const compW = computedWeight(row);
                    return (
                      <tr key={row.key} className="border-b border-slate-100">
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            min="1"
                            className="w-12 rounded border border-slate-300 px-1 py-1 text-right text-xs"
                            value={row.packageNumber}
                            onChange={(e) => updateLine(row.key, { packageNumber: e.target.value })}
                            disabled={readOnly}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <LotPicker
                            value={row.lotId}
                            valueCode={row.lotCode}
                            onChange={(id2, code, productId) => updateLine(row.key, { lotId: id2, lotCode: code, lotProductId: productId, lotAnalisiId: null, lotAnalisiCodice: null, sku: '' })}
                            disabled={readOnly}
                            targetCountry="ES"
                          />
                        </td>
                        <td className="px-2 py-1">
                          <LotAnalisiPicker
                            lotId={row.lotId || null}
                            value={row.lotAnalisiId}
                            valueCodice={row.lotAnalisiCodice}
                            onChange={(id2, codice) => updateLine(row.key, { lotAnalisiId: id2, lotAnalisiCodice: codice })}
                            disabled={readOnly}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <ProductSkuPicker
                            productId={row.lotProductId}
                            value={row.sku}
                            onChange={(sku) => updateLine(row.key, { sku })}
                            disabled={readOnly}
                            dominantGrammatura={dominantGrammatura(row)}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="text"
                            className="w-24 rounded border border-slate-300 px-1 py-1 text-xs"
                            value={row.confezione}
                            onChange={(e) => updateLine(row.key, { confezione: e.target.value })}
                            disabled={readOnly}
                          />
                        </td>
                        {(['qty5g', 'qty10g', 'qty20g', 'qty50g', 'qty100g'] as const).map((field) => (
                          <td key={field} className="px-2 py-1 text-right">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              className="w-12 rounded border border-slate-300 px-1 py-1 text-right text-xs"
                              value={row[field]}
                              onChange={(e) => updateLine(row.key, { [field]: e.target.value })}
                              disabled={readOnly}
                            />
                          </td>
                        ))}
                        <td className="px-2 py-1 text-right">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-20 rounded border border-slate-300 px-1 py-1 text-right text-xs"
                            value={row.qty}
                            placeholder={String(compW)}
                            onChange={(e) => updateLine(row.key, { qty: e.target.value })}
                            disabled={readOnly}
                            title={t('mss.fields.qtyHint', { computed: compW })}
                          />
                        </td>
                        <td className="px-2 py-1 text-right">
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() => removeLine(row.key)}
                              className="text-red-600 hover:underline text-xs"
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addLine}>
              + {t('mss.lines.addRow')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('mss.sections.legend')}</CardTitle>
            {existingLegendQ.data && existingLegendQ.data.total > 0 && (
              <button
                type="button"
                onClick={() => setShowExistingLegend((v) => !v)}
                className="text-xs text-brand-700 hover:underline"
              >
                {showExistingLegend ? t('mss.legend.hideExisting') : t('mss.legend.showExisting', { count: existingLegendQ.data.total })}
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {legend.length === 0 && <p className="text-sm text-slate-500">{t('mss.legend.empty')}</p>}
          {legend.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-2 py-2 w-1/4">{t('mss.legend.key')}</th>
                  <th className="px-2 py-2">{t('mss.legend.value')}</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {legend.map((row) => (
                  <tr key={row.key} className="border-b border-slate-100">
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2 py-1 text-xs font-mono uppercase"
                        value={row.k}
                        onChange={(e) => updateLegend(row.key, { k: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        value={row.v}
                        onChange={(e) => updateLegend(row.key, { v: e.target.value })}
                        disabled={readOnly}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => removeLegend(row.key)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!readOnly && (
            <Button variant="secondary" size="sm" onClick={addLegend}>
              + {t('mss.legend.addRow')}
            </Button>
          )}
          {showExistingLegend && existingLegendQ.data && (
            <div className="mt-3 max-h-48 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2 text-xs">
              <p className="mb-2 font-medium text-slate-700">
                {t('mss.legend.existingTitle', { country })}
              </p>
              <ul className="space-y-1">
                {existingLegendQ.data.items.map((e) => (
                  <li key={e.id} className="flex gap-2">
                    <span className="font-mono text-slate-600">{e.key}</span>
                    <span className="text-slate-400">→</span>
                    <span>{e.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {!readOnly && (
        <div className="flex justify-between gap-2">
          <div>
            {isEdit && (
              <Button
                variant="secondary"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="!bg-red-50 !text-red-700 !border-red-200 hover:!bg-red-100"
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleSave} disabled={isSubmitting}>
              {t('mss.form.saveButton')}
            </Button>
            {isEdit && (
              <Button onClick={handleClose} disabled={isSubmitting}>
                {t('mss.form.closeButton')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
